/**
 * 菜单工具函数
 * 基于权限系统
 */

import * as Icons from '@ant-design/icons';
import React from 'react';
import { permissionUtils } from '@/utils/permissionUtils';
import { getCompleteIconMapping } from './completeIconMapping';
import { enhancedIconConverter } from './enhancedIconConverter'; // 引入增强图标转换器
import { getMenuIntlKey } from './menuTranslationUtils'; // 引入菜单国际化工具
import { enhancedConvertIcon } from './orgMenuIconFix'; // 引入组织管理菜单图标修复补丁

/**
 * 将字符串图标名称转换为图标组件
 * @param iconName 图标名称字符串
 * @returns 对应的图标组件或undefined
 */
export const convertIconString = (
  iconName: string | undefined,
): React.ReactNode => {
  if (!iconName || typeof iconName !== 'string') {
    // 图标名称为空或不是字符串
    return undefined;
  }

  // 首先尝试使用最新的增强图标转换器
  const enhancedResult = enhancedIconConverter(iconName);
  if (enhancedResult) {
    // 使用增强图标转换器成功
    return enhancedResult;
  }

  // 回退到原来的转换逻辑
  // 首先尝试使用组织管理菜单的增强转换函数
  const orgEnhancedResult = enhancedConvertIcon(iconName);
  if (orgEnhancedResult) {
    // 使用组织管理增强转换成功
    return orgEnhancedResult;
  }

  // 清理图标名称
  let cleanIconName = iconName.replace(/['"]/g, '').trim();
  // 清理后的图标名称

  // 使用完整的图标映射表
  const iconMapping = getCompleteIconMapping();

  // 如果是简写名称，转换为完整名称
  if (iconMapping[cleanIconName]) {
    cleanIconName = iconMapping[cleanIconName];
    // 转换简写图标名
  }

  // 查找对应的图标组件
  const IconComponent = (Icons as any)[cleanIconName];
  if (IconComponent) {
    // 找到图标组件
    return React.createElement(IconComponent);
  }

  // 如果找不到，尝试首字母大写的标准格式
  const capitalizedIconName =
    cleanIconName.charAt(0).toUpperCase() + cleanIconName.slice(1);
  const CapitalizedIconComponent = (Icons as any)[capitalizedIconName];
  if (CapitalizedIconComponent) {
    // 找到首字母大写图标组件
    return React.createElement(CapitalizedIconComponent);
  }

  // 最后的兜底方案
  return React.createElement(Icons.QuestionCircleOutlined);
};

/**
 * 将后端菜单数据转换为ProLayout所需的菜单格式
 * @param menus 后端返回的菜单数据
 * @param options 转换选项
 * @returns ProLayout可识别的菜单数据
 */
interface TransformMenuOptions {
  /** 是否包含隐藏菜单(isShow='0') */
  includeHidden?: boolean;
  /** 是否只转换用于导航的菜单 */
  forNavigation?: boolean;
}

export function transformMenus(
  menus: API.AccessMenusItem[],
  options: TransformMenuOptions = {},
): any[] {
  const { includeHidden = false, forNavigation = true } = options;

  // 添加安全检查
  if (!menus) {
    return [];
  }

  if (!Array.isArray(menus)) {
    return [];
  }

  if (menus.length === 0) {
    return [];
  }

  //过滤菜单：启用状态检查和菜单类型检查
  const filteredMenus = menus.filter((menu) => {
    // 添加安全检查
    if (!menu || typeof menu !== 'object') {
      return false;
    }

    //检查是否启用
    if (menu.enabled !== '1') {
      return false;
    }

    // 如果是导航用途，只保留 menu 和 page 类型的菜单
    // function 类型的菜单不显示在侧边栏
    if (forNavigation) {
      const menuType = menu.menuType;
      if (menuType === 'function') {
        return false; //功能类型菜单不显示在导航中
      }
      // menu 和 page 类型的菜单可以显示
    }

    return true;
  });

  // 如果是导航用途，根据 includeHidden 参数决定是否包含隐藏菜单
  const navigationFilteredMenus = forNavigation
    ? includeHidden
      ? filteredMenus // 包含所有启用的菜单（包括隐藏的 isShow='0'）
      : filteredMenus.filter((menu) => menu.isShow !== '0') // 过滤掉隐藏菜单
    : filteredMenus;

  // transformMenus 过滤详情
  // 菜单转换详情

  // 去重：基于 menuPath 去重，保留第一个出现的菜单
  const seenPaths = new Set<string>();
  const uniqueMenus = navigationFilteredMenus.filter((menu) => {
    const path = menu.menuPath;
    if (!path || seenPaths.has(path)) {
      return false;
    }
    seenPaths.add(path);
    return true;
  });

  return uniqueMenus
    .sort((a, b) => (a.weight || 0) - (b.weight || 0)) // 按权重排序
    .map((menu): any => {
      const iconComponent = convertIconString(menu.icon);
      // 菜单转换

      const menuItem: any = {
        name: menu.menuTitle, // 保持原始标题，国际化在更高层处理
        path: menu.menuPath,
        icon: iconComponent,
        // 保存原始菜单信息用于权限检查和其他用途
        __rawMenu: {
          ...menu,
          __displayInfo: {
            isHidden: menu.isShow === '0',
            isVisible: menu.isShow !== '0',
            isEnabled: menu.enabled === '1',
          },
        },
        // 添加国际化键信息
        __intlKey: getMenuIntlKey(menu.menuTitle || ''),
      };

      // 处理子菜单
      if (
        menu.children &&
        Array.isArray(menu.children) &&
        menu.children.length > 0
      ) {
        const childRoutes: any[] = transformMenus(menu.children, options);
        if (childRoutes && childRoutes.length > 0) {
          // ✅ 优化：如果只有一个子菜单，直接返回子菜单而不是父级菜单
          if (childRoutes.length === 1) {
            const singleChild = childRoutes[0];
            // 继承父级菜单的一些属性（如图标）
            return {
              ...singleChild,
              // 如果子菜单没有图标，使用父级菜单的图标
              icon: singleChild.icon || iconComponent,
              // 保留原始菜单信息
              __rawMenu: singleChild.__rawMenu || menuItem.__rawMenu,
              __intlKey: singleChild.__intlKey || menuItem.__intlKey,
            };
          }

          // 多个子菜单，正常处理
          menuItem.routes = childRoutes;
          // 修复：保持父菜单的原始path，不要覆盖为子菜单path
          // menuItem.path = childRoutes[0]?.path || menuItem.path;
        }
      }

      // 处理组件路径
      if (menu.menuComp) {
        menuItem.component = menu.menuComp;
      }

      // 处理外链
      if (menu.isLink === '1' && menu.linkUrl) {
        menuItem.path = menu.linkUrl;
      }

      // 添加隐藏标识（但不影响显示）
      if (menu.isShow === '0') {
        menuItem.__hidden = true;
        menuItem.__displayStatus = 'hidden';
        // 菜单已标记为隐藏但仍然显示
      } else {
        menuItem.__displayStatus = 'visible';
      }

      return menuItem;
    });
}

/**
 * 获取完整的路由配置（包含隐藏菜单）
 * 用于路由配置，包含所有有权访问的菜单项
 * @param menus 菜单数据（已过滤的用户可访问菜单）
 * @returns 完整的路由配置
 */
export const getFullRouteConfig = (menus: API.AccessMenusItem[]) => {
  // 过滤出启用的菜单（包括隐藏菜单）
  const accessibleMenus = menus.filter((menu) => {
    // 检查菜单是否启用
    if (menu.enabled !== '1') return false;

    // 检查用户是否有该菜单权限（通过 menuId 或 menuComp）
    if (!menu.menuId) return false;
    const hasPermission =
      permissionUtils.hasMenuIdPermission(menu.menuId) ||
      (menu.menuComp
        ? permissionUtils.hasMenuCompPermission(menu.menuComp)
        : false);

    return hasPermission;
  });

  // 转换为路由格式，包含隐藏菜单
  return transformMenus(accessibleMenus, {
    includeHidden: true, // 包含隐藏菜单
    forNavigation: false, // 不用于导航显示
  });
};

/**
 * 获取导航菜单（不包含隐藏菜单）
 * 用于侧边栏导航显示
 * @param menus 菜单数据（已过滤的用户可访问菜单）
 * @returns 导航菜单配置
 */
export const getNavigationMenus = (menus: API.AccessMenusItem[]) => {
  // 调试模式：通过环境变量控制，开发环境默认显示所有菜单
  const _isDebugMode =
    process.env.REACT_APP_MENU_DEBUG_MODE === 'true' ||
    (process.env.NODE_ENV === 'development' &&
      process.env.REACT_APP_MENU_DEBUG_MODE !== 'false');

  // 添加安全检查
  if (!menus) {
    return [];
  }

  if (!Array.isArray(menus)) {
    return [];
  }

  // 过滤出需要显示的菜单（用于侧边栏显示）
  // 注意：menus 已经是用户可访问的菜单列表（来自 /api/plat/v1/access/menus）
  const visibleMenus = menus.filter((menu) => {
    // 检查菜单是否启用
    if (menu.enabled !== '1') {
      return false;
    }

    // 检查是否为隐藏菜单 - isShow='0' 的菜单不在侧边栏显示
    if (menu.isShow === '0') {
      return false;
    }

    // 检查菜单类型 - 只显示 menu 和 page 类型
    if (menu.menuType === 'function') {
      return false;
    }

    return true;
  });

  // 转换为导航格式（仅包含侧边栏显示的菜单）
  return transformMenus(visibleMenus, {
    includeHidden: false,
    forNavigation: true,
  });
};

/**
 * 根据 API 返回的菜单数据动态生成路由配置
 * 实现真正的动态菜单控制
 * @param apiMenus API 返回的菜单数据（用户可访问的菜单列表）
 * @returns 动态路由配置
 */
export const generateDynamicRoutes = (apiMenus: API.AccessMenusItem[]) => {
  // 动态路由生成开始
  // API 菜单数据（已包含用户权限信息）

  // 1. 过滤出启用的菜单（包括隐藏菜单）
  const accessibleMenus = apiMenus.filter((menu) => {
    // 必须启用
    if (menu.enabled !== '1') return false;

    // 检查菜单类型 - 只保留 menu 和 page 类型
    if (menu.menuType === 'function') {
      return false;
    }

    return true;
  });

  // 启用的菜单（包括隐藏菜单）

  // 2. 转换为路由格式（包含隐藏菜单，确保路由可用）
  const dynamicRoutes = transformMenus(accessibleMenus, {
    includeHidden: true, // 包含隐藏菜单，确保路由可访问
    forNavigation: true, // 用于导航显示（但会在 transformMenus 中过滤 isShow='0' 的菜单）
  });

  // 生成的动态路由
  // ====================

  return dynamicRoutes;
};

/**
 * 获取扁平化的菜单列表（用于权限校验等场景）
 * @param menus 嵌套的菜单结构
 * @returns 扁平化的菜单数组
 */
export const flattenMenus = (menus: any[]): any[] => {
  const result: any[] = [];

  const traverse = (items: any[]) => {
    items.forEach((item) => {
      result.push(item);
      if (item.routes && item.routes.length > 0) {
        traverse(item.routes);
      }
    });
  };

  traverse(menus);
  return result;
};

/**
 * 递归查找菜单项
 * @param menus 菜单数组
 * @param path 要查找的路径
 * @returns 找到的菜单项或undefined
 */
export const findMenuItem = (menus: any[], path: string): any => {
  for (const menu of menus) {
    if (menu.path === path) {
      return menu;
    }
    if (menu.routes) {
      const found = findMenuItem(menu.routes, path);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};
